import Controller from '@ember/controller';
import { Promise } from 'rsvp';
import { sort } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { showModal } from 'sh-www/utils/modal-controls';
import { PAYOUT_ACCOUNT } from 'sh-www/models/payout-account';

const CLASS_NAME = 'SH-c-admin-user-billing';

class AdminUserBillingController extends Controller {
  @service constants;
  @service store;
  @service('state-managers/application') applicationStateManager;
  @service('commands/payout-account-commands') payoutAccountCommandService;

  get className() {
    return CLASS_NAME;
  }

  /** @type {boolean} */
  _isAddingAccount = false;

  /** @type {Boolean} */
  disableInteraction = false;

  /** @type {Array<String>} */
  payoutAccountsSortOrder = ['createdAt'];

  /** @param {Array<PayoutAccount>} */
  @sort('model.user.payoutAccounts', 'payoutAccountsSortOrder')
  sortedPayoutAccounts;

  /** @type {String} */
  @computed('model.user.{id,fullName}')
  get userName() {
    return `${this.model.user.fullName} (${this.model.user.id})`;
  }

  // TASKS
  @task
  *onCreate({ accountType, accountMethod, status, providerId }) {
    this.set('disableInteraction', true);

    try {
      try {
        yield this.payoutAccountCommandService.createPayoutAccount({
          user: this.model.user,
          accountType,
          accountMethod,
          status,
          providerId
        });
      } catch(e) {
        handleErrorResponseWithModal(this, e);
        return;
      }

      const user = yield this.store.findRecord('user', this.model.user.id, {
        include: 'payout-accounts',
        reload: true,
      });

      this.set('model.user', user);

      this.set('_isAddingAccount', false);
      this.applicationStateManager.addToast('Account Created');
    } finally {
      this.set('disableInteraction', false);
    }
  }

  @task
  *onDelete(payoutAccount) {
    yield new Promise((resolve, reject) => {
      const payoutAccountsCount = this.sortedPayoutAccounts.length;
      const promptText = payoutAccountsCount > 1
        ? 'This account will be removed immediately. Are you sure you want to remove it?'
        : "WARNING! This is the user's last remaining payout account.  Are you sure you want to remove it?";

      // Confirm before we proceed
      showModal(this, 'confirmation', {
        isShowCancelButton: true,
        titleText: 'Are you sure?',
        bodyText: promptText,
        confirmAction: () => {
          resolve(true);
        },
        cancelAction: () => {
          reject(new Error('Deletion cancelled.'));
        }
      });
    });

    this.set('disableInteraction', true);

    try {
      try {
        yield this.payoutAccountCommandService.deletePayoutAccount(payoutAccount);
      } catch(e) {
        handleErrorResponseWithModal(this, e);
        return;
      }

      const user = yield this.store.findRecord('user', this.model.user.id, {
        include: 'payout-accounts',
        reload: true,
      });

      this.set('model.user', user);

      this.applicationStateManager.addToast('Account Deleted');
    } finally {
      this.set('disableInteraction', false);
    }
  }

  @task
  *onSetPreferred(payoutAccount) {
    if (payoutAccount.status !== PAYOUT_ACCOUNT.STATUSES.ACTIVE) {
      yield new Promise((resolve, reject) => {
        // Confirm before we proceed
        showModal(this, 'confirmation', {
          isShowCancelButton: true,
          titleText: 'Are you sure?',
          bodyText: 'This account is not marked as active. If the account is not active on the provider side, payments will fail.',
          confirmAction: () => {
            resolve(true);
          },
          cancelAction: () => {
            reject(new Error('Set preferred cancelled.'));
          }
        });
      });
    }

    this.set('disableInteraction', true);

    try {
      try {
        yield this.payoutAccountCommandService.setPayoutAccountPreferred(payoutAccount);
      } catch(e) {
        handleErrorResponseWithModal(this, e);
        return;
      }

      const user = yield this.store.findRecord('user', this.model.user.id, {
        include: 'payout-accounts',
        reload: true,
      });

      this.set('model.user', user);

      this.applicationStateManager.addToast('Account Updated');
    } finally {
      this.set('disableInteraction', false);
    }
  }

  @task
  *onSetProviderId(payoutAccount, providerId) {
    this.set('disableInteraction', true);

    if (providerId === payoutAccount.providerId) {
      return;
    }

    try {
      try {
        yield this.payoutAccountCommandService.setPayoutAccountProviderId(payoutAccount, providerId);
      } catch (e) {
        handleErrorResponseWithModal(this, e);
        return true;
      }

      const user = yield this.store.findRecord('user', this.model.user.id, {
        include: 'payout-accounts',
        reload: true,
      });

      this.set('model.user', user);

      this.applicationStateManager.addToast('Provider ID updated');
    } finally {
      this.set('disableInteraction', false);
    }
  }

  @task
  *onSetStatus(payoutAccount, status) {
    if (status === payoutAccount.status) return;

    yield new Promise((resolve, reject) => {
      // Confirm before we proceed
      showModal(this, 'confirmation', {
        isShowCancelButton: true,
        titleText: 'Are you sure?',
        bodyText: 'If the status you set is not aligned with the provider, payments could fail!',
        confirmAction: () => {
          resolve(true);
        },
        cancelAction: () => {
          reject(new Error('Set status cancelled.'));
        }
      });
    });

    this.set('disableInteraction', true);

    try {
      try {
        yield this.payoutAccountCommandService.setPayoutAccountStatus(payoutAccount, status);
      } catch (e) {
        handleErrorResponseWithModal(this, e);
        return true;
      }

      const user = yield this.store.findRecord('user', this.model.user.id, {
        include: 'payout-accounts',
        reload: true,
      });

      this.set('model.user', user);

      this.applicationStateManager.addToast('Status updated');
    } finally {
      this.set('disableInteraction', false);
    }
  }

  @action
  handleCreate({ accountType, accountMethod, status, providerId }) {
    return this.onCreate.perform({ accountType, accountMethod, status, providerId });
  }

  @action
  handleRemove(payoutAccount) {
    this.onDelete.perform(payoutAccount);
  }

  @action
  handleSelect(payoutAccount) {
    this.onSetPreferred.perform(payoutAccount);
  }

  @action
  handleEdit(payoutAccount) {
    showModal(this, 'settings/edit-payout-account', {
      payoutAccount,
      onSetProviderId: this.onSetProviderId,
      onSetStatus: this.onSetStatus,
    });
  }

  @action
  addAccount() {
    this.set('_isAddingAccount', true);
  }
}

export default AdminUserBillingController;
